import { clsx } from 'clsx'
import { useCompetitionTeams } from '@/features/squad/queries'
import type { PersonsFilter, PersonsPositionFilter } from '../queries'
import styles from './PersonsFilters.module.css'

type PersonsFiltersProps = {
  competitionId: string | null
  filter:        PersonsFilter
  onChange:      (next: PersonsFilter) => void
}

const POSITION_OPTIONS: { value: PersonsPositionFilter; label: string }[] = [
  { value: 'GK',   label: 'GOL' },
  { value: 'DEF',  label: 'DEF' },
  { value: 'MID',  label: 'MEI' },
  { value: 'FWD',  label: 'ATA' },
  { value: 'NONE', label: 'Sem posição' },
]

export function PersonsFilters({ competitionId, filter, onChange }: PersonsFiltersProps) {
  const { data: teams, isPending, isError } = useCompetitionTeams(competitionId ?? '')

  const hasFilter = !!(filter.teamId || filter.position)

  function setTeam(teamId: string) {
    const next: PersonsFilter = {}
    if (teamId)          next.teamId   = teamId
    if (filter.position) next.position = filter.position
    onChange(next)
  }

  function togglePosition(position: PersonsPositionFilter) {
    const next: PersonsFilter = {}
    if (filter.teamId)        next.teamId   = filter.teamId
    if (filter.position !== position) next.position = position
    onChange(next)
  }

  function clearAll() {
    onChange({})
  }

  return (
    <div className={styles.wrap}>
      <div className={styles.row}>
        <label className={styles.field}>
          <span className={styles.label}>Time</span>
          <select
            className={styles.select}
            value={filter.teamId ?? ''}
            onChange={(e) => setTeam(e.target.value)}
            disabled={!competitionId || isPending || isError}
          >
            <option value="">Todos os times</option>
            {teams?.map((t) => (
              <option key={t.id} value={t.id}>
                {t.code} — {t.name}
              </option>
            ))}
          </select>
        </label>

        <div className={styles.field}>
          <span className={styles.label}>Posição</span>
          <div className={styles.posGroup} role="group" aria-label="Filtrar por posição">
            {POSITION_OPTIONS.map(({ value, label }) => (
              <button
                key={value}
                type="button"
                className={clsx(
                  styles.posBtn,
                  filter.position === value && styles.posBtnActive,
                  value === 'NONE' && styles.posBtnNone,
                )}
                onClick={() => togglePosition(value)}
                aria-pressed={filter.position === value}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {hasFilter && (
          <button type="button" className={styles.clearBtn} onClick={clearAll}>
            Limpar filtros
          </button>
        )}
      </div>

      {!hasFilter && (
        <p className={styles.hint}>
          Selecione um time ou uma posição para carregar a lista.
        </p>
      )}
    </div>
  )
}
